import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useTranslation } from 'react-i18next';
import { globalLogOut, refreshTokens } from '@/lib/auth/authServices';
import Storage, { STORAGE_KEYS } from '@/utils/helpers/Storage';
import { clearUsageTimeLimitLockout } from '@/lib/auth/usageTimeLimitLockout';

const MainContent = styled(Box)(
  () => `
    height: 100%;
    display: flex;
    flex: 1;
    overflow: auto;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`,
);

const UsageTimeLimitLockoutPage = () => {
  const { t: tCommon } = useTranslation('common');
  const [isRetrying, setIsRetrying] = useState(false);

  const handleTryAgain = async () => {
    if (isRetrying) return;
    setIsRetrying(true);

    try {
      const refreshToken = Storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);
      if (refreshToken) {
        const data = await refreshTokens(refreshToken);
        if (data?.access_token) {
          Storage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
        }
      }
      clearUsageTimeLimitLockout();
      window.location.reload();
    } catch {
      setIsRetrying(false);
    }
  };

  const handleSignOut = () => {
    globalLogOut();
  };

  return (
    <>
      <Helmet>
        <title>{tCommon('ERRORS.USAGE_TIME_LIMIT_META_TITLE')}</title>
      </Helmet>
      <MainContent>
        <Container maxWidth="md">
          <Box sx={{
            textAlign: 'center',
          }}
          >
            <Box
              component="img"
              src="/static/images/status/error.webp"
              alt=""
              sx={{ width: '60%' }}
            />
            <Typography variant="h2" sx={{ my: 2 }}>
              {tCommon('ERRORS.USAGE_TIME_LIMIT_TITLE')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                color: 'text.secondary',
                fontWeight: 'normal',
              }}
            >
              {tCommon('ERRORS.USAGE_TIME_LIMIT_MESSAGE')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 4,
                color: 'text.secondary',
              }}
            >
              {tCommon('ERRORS.USAGE_TIME_LIMIT_HINT')}
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button
                variant="contained"
                onClick={handleTryAgain}
                disabled={isRetrying}
                startIcon={isRetrying
                  ? (
                    <CircularProgress
                      size={16}
                      color="inherit"
                      aria-label={tCommon('ACCESSIBILITY.LOADING')}
                    />
                  )
                  : undefined}
              >
                {tCommon('ERRORS.USAGE_TIME_LIMIT_TRY_AGAIN')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleSignOut}
                disabled={isRetrying}
              >
                {tCommon('HEADER_USER_BOX.SIGN_OUT')}
              </Button>
            </Stack>
          </Box>
        </Container>
      </MainContent>
    </>
  );
};

export default UsageTimeLimitLockoutPage;
